/**
 * Channels View — Connect and manage messaging platform integrations.
 *
 * Three states:
 *   1. Channel list (default) — shows connected channels
 *   2. Platform picker — choose which platform to add
 *   3. Setup wizard — step-by-step guided credential entry
 *   4. Channel detail — shows config, status, actions for a connected channel
 */

import { LitElement, html, nothing, type TemplateResult } from "lit";
import { customElement, state } from "lit/decorators.js";
import {
  getServices,
  type ChannelInfo,
  type ChannelSetupConfig,
  type ChannelSetupStep,
  type ChannelSetupVariant,
} from "../services/index.ts";
import { CHANNEL_VARIANTS } from "../services/channel-setup-configs.ts";
import { PLATFORMS } from "../components/platform-icons.ts";

/** Resolve platform icon metadata, handling variant types like whatsapp-personal → whatsapp */
function getPlatformMeta(type: string) {
  return PLATFORMS[type as keyof typeof PLATFORMS]
    ?? PLATFORMS[type.split("-")[0] as keyof typeof PLATFORMS];
}

type ViewState =
  | { kind: "list" }
  | { kind: "picker" }
  | { kind: "variant-picker"; platformType: string; variants: ChannelSetupVariant[] }
  | { kind: "wizard"; config: ChannelSetupConfig; stepIndex: number }
  | { kind: "detail"; channel: ChannelInfo };

@customElement("katanoid-channels-view")
export class KatanoidChannelsView extends LitElement {
  protected override createRenderRoot(): HTMLElement {
    return this;
  }

  @state() private channels: ChannelInfo[] = [];
  @state() private loading = true;
  @state() private view: ViewState = { kind: "list" };

  // Wizard state
  @state() private wizardCredentials: Record<string, string> = {};
  @state() private wizardValidating = false;
  @state() private wizardError: string | null = null;
  @state() private wizardSuccess = false;

  // QR pairing state (WhatsApp Personal)
  @state() private qrState: "waiting" | "showing" | "connected" | null = null;
  @state() private qrData: string | null = null;
  @state() private qrChannelId: string | null = null;
  private qrPollTimer: ReturnType<typeof setInterval> | null = null;

  // Detail state
  @state() private detailTesting = false;
  @state() private detailTestResult: string | null = null;
  @state() private detailRemoving = false;
  @state() private detailRefreshing = false;

  override connectedCallback(): void {
    super.connectedCallback();
    this.loadChannels();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.stopQrPolling();
  }

  private async loadChannels(): Promise<void> {
    this.loading = true;
    try {
      const { channels } = getServices();
      this.channels = await channels.listChannels();
    } catch {
      this.channels = [];
    }
    this.loading = false;
  }

  // ── Main render ──

  protected override render(): TemplateResult {
    if (this.view.kind === "picker") return this.renderPicker();
    if (this.view.kind === "variant-picker") return this.renderVariantPicker(this.view.platformType, this.view.variants);
    if (this.view.kind === "wizard") return this.renderWizard(this.view.config, this.view.stepIndex);
    if (this.view.kind === "detail") return this.renderDetail(this.view.channel);
    return this.renderList();
  }

  // ── List view ──

  private renderList(): TemplateResult {
    return html`
      <div class="main-content-padded view-enter">
        <div class="view-header">
          <h1 class="view-title">Channels</h1>
          <button class="btn btn--primary btn--sm" @click=${() => { this.view = { kind: "picker" }; }}>
            + Add Channel
          </button>
        </div>

        ${this.loading
          ? html`
              <div class="channel-wizard-list">
                <div class="shimmer shimmer--card"></div>
                <div class="shimmer shimmer--card"></div>
                <div class="shimmer shimmer--card"></div>
              </div>
            `
          : this.channels.length === 0
            ? this.renderEmptyState()
            : this.renderChannelList()}
      </div>
    `;
  }

  private renderEmptyState(): TemplateResult {
    return html`
      <div class="channels-empty">
        <h2 class="channels-empty-title">Connect your messaging platforms</h2>
        <p class="channels-empty-desc">
          Your assistant works across every platform.
          Messages sync automatically \u2014 one conversation, everywhere you chat.
        </p>

        <button class="btn btn--primary channels-empty-cta" @click=${() => { this.view = { kind: "picker" }; }}>
          + Add a channel
        </button>

        <div class="channels-empty-grid">
          ${Object.values(PLATFORMS).map(
            (meta) => html`
              <div class="channels-empty-grid-item">
                <span class="channels-empty-grid-icon" .innerHTML=${meta.svg}></span>
                <span class="channels-empty-grid-name">${meta.name}</span>
              </div>
            `
          )}
        </div>
      </div>
    `;
  }

  private renderChannelList(): TemplateResult {
    return html`
      <div class="stagger-children channel-list">
        ${this.channels.map((ch) => {
          const meta = getPlatformMeta(ch.type);

          return html`
            <div class="channel-card" @click=${() => { this.view = { kind: "detail", channel: ch }; }}>
              <div class="channel-card-icon channel-card-icon--tinted" style="color: ${meta?.color ?? 'var(--text-tertiary)'};">
                <span .innerHTML=${meta?.svg ?? ''}></span>
              </div>
              <div class="channel-card-info">
                <div class="channel-card-name">${ch.displayName}</div>
                <div class="channel-card-status">
                  <span class="status-dot status-dot--${ch.status}"></span>
                  ${ch.status === "connected"
                    ? html`Connected${ch.botUsername ? html` &middot; ${ch.botUsername}` : nothing}`
                    : ch.status === "connecting"
                      ? "Connecting..."
                      : ch.status === "error"
                        ? html`Error${ch.errorMessage ? html` &middot; ${ch.errorMessage}` : nothing}`
                        : "Disconnected"}
                </div>
              </div>
              <span class="channel-card-chevron">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
              </span>
            </div>
          `;
        })}
      </div>
    `;
  }

  // ── Platform picker ──

  private renderPicker(): TemplateResult {
    // Channels with setup configs (supported for real connection)
    const supportedTypes = new Set(["telegram", "discord", "slack", "whatsapp"]);
    const available = Object.entries(PLATFORMS)
      .filter(([id]) => supportedTypes.has(id))
      .filter(([id]) => {
        // For platforms with variants, check if ALL variant types are taken
        const variants = CHANNEL_VARIANTS[id];
        if (variants) {
          return variants.some((v) => !this.channels.some((c) => c.type === v.type));
        }
        return !this.channels.some((c) => c.type === id);
      });

    const comingSoon = Object.entries(PLATFORMS)
      .filter(([id]) => !supportedTypes.has(id))
      .filter(([id]) => !this.channels.some((c) => c.type === id));

    return html`
      <div class="main-content-padded view-enter">
        <div class="view-header">
          <button class="btn btn--ghost btn--sm" @click=${() => { this.view = { kind: "list" }; }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
            Back
          </button>
          <h1 class="view-title">Add a Channel</h1>
        </div>

        ${available.length > 0 ? html`
          <div class="channel-wizard-list">
            ${available.map(
              ([id, meta]) => html`
                <div class="channel-card" @click=${() => this.startWizard(id)}>
                  <div class="channel-card-icon channel-card-icon--tinted" style="color: ${meta.color};">
                    <span .innerHTML=${meta.svg}></span>
                  </div>
                  <div class="channel-card-info">
                    <div class="channel-card-name">${meta.name}</div>
                    <div class="channel-card-desc">${meta.description}</div>
                  </div>
                  <span class="channel-card-chevron">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                  </span>
                </div>
              `
            )}
          </div>
        ` : nothing}

        ${comingSoon.length > 0 ? html`
          <div style="margin-top: 24px;">
            <h3 style="color: var(--text-secondary); font-size: 13px; font-weight: 500; margin-bottom: 12px;">Coming soon</h3>
            <div class="channel-wizard-list" style="opacity: 0.5; pointer-events: none;">
              ${comingSoon.map(
                ([, meta]) => html`
                  <div class="channel-card">
                    <div class="channel-card-icon channel-card-icon--tinted" style="color: ${meta.color};">
                      <span .innerHTML=${meta.svg}></span>
                    </div>
                    <div class="channel-card-info">
                      <div class="channel-card-name">${meta.name}</div>
                      <div class="channel-card-desc">${meta.description}</div>
                    </div>
                  </div>
                `
              )}
            </div>
          </div>
        ` : nothing}
      </div>
    `;
  }

  // ── Variant picker (e.g. WhatsApp: Business vs Personal) ──

  private renderVariantPicker(platformType: string, variants: ChannelSetupVariant[]): TemplateResult {
    const meta = PLATFORMS[platformType as keyof typeof PLATFORMS];
    // Filter out variants already connected
    const availableVariants = variants.filter(
      (v) => !this.channels.some((c) => c.type === v.type)
    );

    return html`
      <div class="main-content-padded view-enter">
        <div class="view-header">
          <button class="btn btn--ghost btn--sm" @click=${() => { this.view = { kind: "picker" }; }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
            Back
          </button>
          <h1 class="view-title">Connect ${meta?.name ?? platformType}</h1>
        </div>

        <p style="color: var(--text-secondary); font-size: 14px; margin: 0 0 20px 0;">
          Choose how you want to connect WhatsApp:
        </p>

        <div class="channel-variant-list">
          ${availableVariants.map((v) => html`
            <div class="channel-variant-card" @click=${() => this.startWizardForType(v.type)}>
              <div class="channel-variant-header">
                <span class="channel-variant-label">${v.label}</span>
                ${v.badge ? html`<span class="channel-variant-badge">${v.badge}</span>` : nothing}
              </div>
              <p class="channel-variant-desc">${v.description}</p>
              <span class="channel-card-chevron">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
              </span>
            </div>
          `)}
        </div>
      </div>
    `;
  }

  // ── Setup wizard ──

  private startWizard(type: string): void {
    // Check if this platform has variants (e.g. WhatsApp has Official + Personal)
    const variants = CHANNEL_VARIANTS[type];
    if (variants) {
      this.view = { kind: "variant-picker", platformType: type, variants };
      return;
    }

    this.startWizardForType(type);
  }

  private startWizardForType(type: string): void {
    const { channels } = getServices();
    const config = channels.getSetupConfig(type);
    this.wizardCredentials = {};
    this.wizardValidating = false;
    this.wizardError = null;
    this.wizardSuccess = false;
    // Reset QR pairing state
    this.stopQrPolling();
    this.qrState = null;
    this.qrData = null;
    this.qrChannelId = null;
    this.view = { kind: "wizard", config, stepIndex: 0 };
  }

  private renderWizard(config: ChannelSetupConfig, stepIndex: number): TemplateResult {
    const step = config.steps[stepIndex];
    const isLastStep = stepIndex === config.steps.length - 1;
    const hasAllCredentials = config.credentialKeys.every(
      (k) => (this.wizardCredentials[k] ?? "").trim().length > 0
    );

    return html`
      <div class="main-content-padded view-enter">
        <div class="view-header">
          <button class="btn btn--ghost btn--sm" @click=${() => this.wizardBack(config, stepIndex)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
            Back
          </button>
          <h1 class="view-title">Connect ${config.name}</h1>
        </div>

        <!-- Progress dots -->
        <div style="display: flex; gap: 6px; margin-bottom: 24px;">
          ${config.steps.map((_, i) => html`
            <div style="
              width: 8px; height: 8px; border-radius: 50%;
              background: ${i <= stepIndex ? 'var(--accent)' : 'var(--border)'};
              transition: background 0.2s ease;
            "></div>
          `)}
        </div>

        ${this.wizardSuccess
          ? this.renderWizardSuccess(config)
          : this.qrState
            ? this.renderQrStep(config)
            : this.renderWizardStep(step, isLastStep, hasAllCredentials)}
      </div>
    `;
  }

  private renderWizardStep(step: ChannelSetupStep, isLastStep: boolean, hasAllCredentials: boolean): TemplateResult {
    return html`
      <div class="card" style="padding: 20px;">
        <h2 style="font-size: 17px; font-weight: 600; margin: 0 0 8px 0; color: var(--text-primary);">
          ${step.title}
        </h2>
        <p style="font-size: 14px; color: var(--text-secondary); margin: 0 0 16px 0; line-height: 1.5;">
          ${step.description}
        </p>

        ${step.inputKey ? html`
          <div class="automation-form-field" style="margin-top: 16px;">
            <label class="input-label">${step.inputLabel ?? "Value"}</label>
            <input
              class="input"
              type="${step.inputType ?? "text"}"
              placeholder="${step.inputPlaceholder ?? ""}"
              .value=${this.wizardCredentials[step.inputKey] ?? ""}
              @input=${(e: InputEvent) => {
                this.wizardCredentials = {
                  ...this.wizardCredentials,
                  [step.inputKey!]: (e.target as HTMLInputElement).value,
                };
              }}
            />
          </div>
        ` : nothing}

        ${this.wizardError ? html`
          <div class="automation-error" style="margin-top: 12px;">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
            ${this.wizardError}
          </div>
        ` : nothing}

        <div style="display: flex; gap: 8px; margin-top: 20px; justify-content: flex-end;">
          ${isLastStep && hasAllCredentials ? html`
            <button
              class="btn btn--primary"
              ?disabled=${this.wizardValidating}
              @click=${this.wizardConnect}
            >
              ${this.wizardValidating
                ? html`<div class="spinner spinner--sm"></div> Connecting...`
                : "Connect"}
            </button>
          ` : html`
            <button
              class="btn btn--primary"
              ?disabled=${step.inputKey ? !(this.wizardCredentials[step.inputKey] ?? "").trim() : false}
              @click=${this.wizardNext}
            >
              Continue
            </button>
          `}
        </div>
      </div>
    `;
  }

  private renderWizardSuccess(config: ChannelSetupConfig): TemplateResult {
    return html`
      <div class="card" style="padding: 24px; text-align: center;">
        <div style="font-size: 48px; margin-bottom: 12px;">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
        </div>
        <h2 style="font-size: 20px; font-weight: 600; margin: 0 0 8px 0; color: var(--text-primary);">
          ${config.name} connected!
        </h2>
        <p style="font-size: 14px; color: var(--text-secondary); margin: 0 0 20px 0;">
          Your assistant is now available on ${config.name}. Messages will appear in your chat history.
        </p>

        ${config.type === "whatsapp-personal" ? html`
          <div style="
            background: var(--surface-secondary, #f5f5f5);
            border-radius: 10px;
            padding: 16px;
            margin: 0 0 20px 0;
            text-align: left;
          ">
            <p style="font-size: 13px; color: var(--text-secondary); margin: 0; line-height: 1.5;">
              A <strong>Katanoid</strong> group has been created in your WhatsApp.
              Send messages there to chat with your assistant.
              Automations and reminders will also be delivered to this group.
            </p>
          </div>
        ` : nothing}

        <button class="btn btn--primary" @click=${() => {
          this.view = { kind: "list" };
          this.loadChannels();
        }}>
          Done
        </button>
      </div>
    `;
  }

  private wizardBack(config: ChannelSetupConfig, stepIndex: number): void {
    this.wizardError = null;
    if (stepIndex > 0) {
      this.view = { kind: "wizard", config, stepIndex: stepIndex - 1 };
    } else {
      // If this type has variants, go back to the variant picker
      // Resolve the base platform type (e.g. "whatsapp-personal" → "whatsapp")
      const baseType = config.type.split("-")[0];
      const variants = CHANNEL_VARIANTS[baseType];
      if (variants) {
        this.view = { kind: "variant-picker", platformType: baseType, variants };
      } else {
        this.view = { kind: "picker" };
      }
    }
  }

  private wizardNext = (): void => {
    if (this.view.kind !== "wizard") return;
    this.wizardError = null;
    this.view = { ...this.view, stepIndex: this.view.stepIndex + 1 };
  };

  private wizardConnect = async (): Promise<void> => {
    if (this.view.kind !== "wizard") return;
    const config = this.view.config;

    // WhatsApp Personal uses QR pairing flow (no credentials)
    if (config.type === "whatsapp-personal") {
      return this.startQrPairing(config);
    }

    this.wizardValidating = true;
    this.wizardError = null;

    try {
      const { channels } = getServices();

      // First validate
      const validation = await channels.validateCredentials(
        config.type,
        this.wizardCredentials
      );

      if (!validation.valid) {
        this.wizardError = validation.error ?? "Invalid credentials. Please check and try again.";
        this.wizardValidating = false;
        return;
      }

      // Then add
      await channels.addChannel(config.type, this.wizardCredentials);
      this.wizardSuccess = true;

      // Notify parent to refresh sidebar
      this.dispatchEvent(
        new CustomEvent("channels-changed", { bubbles: true, composed: true })
      );
    } catch (err) {
      this.wizardError = (err as Error).message || "Failed to connect. Please try again.";
    }

    this.wizardValidating = false;
  };

  // ── QR Pairing (WhatsApp Personal) ──

  private async startQrPairing(_config: ChannelSetupConfig): Promise<void> {
    this.wizardValidating = true;
    this.wizardError = null;
    this.qrState = "waiting";
    this.qrData = null;

    try {
      const { channels } = getServices();

      // Create the channel with no credentials — gateway will start pairing
      const channel = await channels.addChannel("whatsapp-personal");
      this.qrChannelId = channel.id;

      // Start polling for QR code and status updates
      this.startQrPolling(channel.id);
    } catch (err) {
      this.wizardError = (err as Error).message || "Failed to start pairing.";
      this.qrState = null;
    }

    this.wizardValidating = false;
  }

  private startQrPolling(channelId: string): void {
    this.stopQrPolling();

    this.qrPollTimer = setInterval(async () => {
      try {
        const { channels } = getServices();
        const ch = await channels.getChannelStatus(channelId);

        if (!ch) return; // Channel not found yet, keep polling

        const { status, config, errorMessage } = ch;

        // QR code available
        const qr = config?.qr as string | undefined;
        if (qr && qr !== this.qrData) {
          this.qrData = qr;
          this.qrState = "showing";
        }

        // Connected!
        if (status === "connected") {
          this.qrState = "connected";
          this.wizardSuccess = true;
          this.stopQrPolling();
          this.dispatchEvent(
            new CustomEvent("channels-changed", { bubbles: true, composed: true })
          );
        }

        // Error
        if (status === "error") {
          this.wizardError = errorMessage || "Connection failed";
          this.qrState = null;
          this.stopQrPolling();
        }

        // Disconnected (logged out)
        if (status === "disconnected" && this.qrState !== "waiting") {
          this.wizardError = errorMessage || "WhatsApp session was disconnected";
          this.qrState = null;
          this.stopQrPolling();
        }
      } catch (err) {
        console.warn("[QR Poll] Error:", err);
      }
    }, 2000);
  }

  private stopQrPolling(): void {
    if (this.qrPollTimer) {
      clearInterval(this.qrPollTimer);
      this.qrPollTimer = null;
    }
  }

  private renderQrStep(config: ChannelSetupConfig): TemplateResult {
    return html`
      <div class="card" style="padding: 24px;">
        <h2 style="font-size: 17px; font-weight: 600; margin: 0 0 8px 0; color: var(--text-primary);">
          ${this.qrState === "waiting" ? "Preparing QR Code..." : "Scan QR Code"}
        </h2>

        ${this.qrState === "waiting" ? html`
          <div style="text-align: center; padding: 40px 0;">
            <div class="spinner"></div>
            <p style="color: var(--text-secondary); margin-top: 16px; font-size: 14px;">
              Preparing your QR code. This may take up to 30 seconds...
            </p>
          </div>
        ` : nothing}

        ${this.qrState === "showing" && this.qrData ? html`
          <p style="font-size: 14px; color: var(--text-secondary); margin: 0 0 20px 0; line-height: 1.5;">
            Open WhatsApp on your phone, go to
            <strong>Settings &rarr; Linked Devices &rarr; Link a Device</strong>,
            and scan the code below.
          </p>

          <div style="
            display: flex; justify-content: center; padding: 20px;
            background: white; border-radius: 12px; margin: 0 auto;
            max-width: 280px;
          ">
            <img
              src="https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(this.qrData)}"
              alt="WhatsApp QR Code"
              width="240"
              height="240"
              style="image-rendering: pixelated;"
            />
          </div>

          <p style="text-align: center; color: var(--text-tertiary); font-size: 12px; margin-top: 12px;">
            QR code refreshes automatically. Keep this page open.
          </p>
        ` : nothing}

        ${this.wizardError ? html`
          <div class="automation-error" style="margin-top: 12px;">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
            ${this.wizardError}
          </div>
        ` : nothing}

        <div style="display: flex; gap: 8px; margin-top: 20px; justify-content: flex-end;">
          <button class="btn btn--ghost btn--sm" @click=${() => {
            this.stopQrPolling();
            // Clean up the created channel if we're cancelling
            if (this.qrChannelId) {
              const { channels } = getServices();
              channels.removeChannel(this.qrChannelId).catch(() => {});
              this.qrChannelId = null;
            }
            this.qrState = null;
            this.qrData = null;
          }}>
            Cancel
          </button>
        </div>
      </div>
    `;
  }

  // ── Channel detail ──

  private renderDetail(channel: ChannelInfo): TemplateResult {
    const meta = getPlatformMeta(channel.type);

    return html`
      <div class="main-content-padded view-enter">
        <div class="view-header">
          <button class="btn btn--ghost btn--sm" @click=${() => { this.view = { kind: "list" }; this.detailTestResult = null; }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
            Back
          </button>
          <h1 class="view-title">${channel.displayName}</h1>
        </div>

        <div class="card" style="padding: 20px;">
          <!-- Status -->
          <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 20px;">
            <div class="channel-card-icon channel-card-icon--tinted" style="color: ${meta?.color ?? 'var(--text-tertiary)'}; width: 40px; height: 40px;">
              <span .innerHTML=${meta?.svg ?? ''}></span>
            </div>
            <div>
              <div style="font-size: 15px; font-weight: 600; color: var(--text-primary);">
                ${channel.displayName}
                ${channel.botUsername ? html`<span style="font-weight: 400; color: var(--text-secondary);"> &middot; ${channel.botUsername}</span>` : nothing}
              </div>
              <div class="channel-card-status" style="margin-top: 2px;">
                <span class="status-dot status-dot--${channel.status}"></span>
                ${channel.status === "connected" ? "Connected" : channel.status === "error" ? "Error" : channel.status === "connecting" ? "Connecting..." : "Disconnected"}
                ${channel.connectedAt ? html`<span style="color: var(--text-tertiary); margin-left: 8px;">since ${new Date(channel.connectedAt).toLocaleDateString()}</span>` : nothing}
              </div>
            </div>
          </div>

          ${channel.errorMessage ? html`
            <div class="automation-error" style="margin-bottom: 16px;">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
              ${channel.errorMessage}
            </div>
          ` : nothing}

          <!-- Info rows -->
          <div style="display: flex; flex-direction: column; gap: 12px; border-top: 1px solid var(--border); padding-top: 16px;">
            ${channel.lastActivityAt ? html`
              <div style="display: flex; justify-content: space-between;">
                <span style="color: var(--text-secondary); font-size: 13px;">Last activity</span>
                <span style="color: var(--text-primary); font-size: 13px;">${this.fmtRelative(channel.lastActivityAt)}</span>
              </div>
            ` : nothing}
            <div style="display: flex; justify-content: space-between;">
              <span style="color: var(--text-secondary); font-size: 13px;">Added</span>
              <span style="color: var(--text-primary); font-size: 13px;">${new Date(channel.createdAt).toLocaleDateString()}</span>
            </div>
          </div>

          ${this.detailTestResult ? html`
            <div class="${this.detailTestResult === 'ok' ? '' : 'automation-error'}" style="margin-top: 16px; padding: 8px 12px; border-radius: 8px; font-size: 13px; ${this.detailTestResult === 'ok' ? 'background: var(--bg-success, rgba(48,209,88,0.1)); color: var(--text-success, #30d158);' : ''}">
              ${this.detailTestResult === "ok" ? "Connection test passed" : this.detailTestResult}
            </div>
          ` : nothing}

          <!-- Actions -->
          <div style="display: flex; gap: 8px; margin-top: 20px; border-top: 1px solid var(--border); padding-top: 16px;">
            <button
              class="btn btn--ghost btn--sm"
              ?disabled=${this.detailTesting}
              @click=${() => this.handleTestChannel(channel)}
            >
              ${this.detailTesting ? html`<div class="spinner spinner--sm"></div>` : "Test Connection"}
            </button>
            <button
              class="btn btn--ghost btn--sm"
              ?disabled=${this.detailRefreshing}
              @click=${() => this.handleRefreshWebhook(channel)}
            >
              ${this.detailRefreshing ? html`<div class="spinner spinner--sm"></div>` : "Refresh Webhook"}
            </button>
            <button
              class="btn btn--ghost btn--sm session-card-delete"
              ?disabled=${this.detailRemoving}
              @click=${() => this.handleRemoveChannel(channel)}
            >
              ${this.detailRemoving ? html`<div class="spinner spinner--sm"></div>` : "Disconnect"}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  // ── Detail handlers ──

  private handleTestChannel = async (channel: ChannelInfo): Promise<void> => {
    this.detailTesting = true;
    this.detailTestResult = null;
    try {
      const { channels } = getServices();
      const result = await channels.testChannel(channel.id);
      this.detailTestResult = result.ok ? "ok" : result.error ?? "Test failed";
    } catch {
      this.detailTestResult = "Test failed";
    }
    this.detailTesting = false;
  };

  private handleRefreshWebhook = async (channel: ChannelInfo): Promise<void> => {
    this.detailRefreshing = true;
    this.detailTestResult = null;
    try {
      const { channels } = getServices();
      const result = await channels.refreshWebhook(channel.id);
      this.detailTestResult = result.ok ? "ok" : result.error ?? "Refresh failed";
    } catch {
      this.detailTestResult = "Refresh failed";
    }
    this.detailRefreshing = false;
  };

  private handleRemoveChannel = async (channel: ChannelInfo): Promise<void> => {
    this.detailRemoving = true;
    try {
      const { channels } = getServices();
      await channels.removeChannel(channel.id);
      this.view = { kind: "list" };
      await this.loadChannels();
      this.dispatchEvent(
        new CustomEvent("channels-changed", { bubbles: true, composed: true })
      );
    } catch {
      // Handle error
    }
    this.detailRemoving = false;
  };

  // ── Formatting ──

  private fmtRelative(iso: string): string {
    const diff = Date.now() - new Date(iso).getTime();
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "katanoid-channels-view": KatanoidChannelsView;
  }
}
